// The Live dashboard's arithmetic (pages/Floor.tsx): the quarter-hour series,
// chart geometry, SLA levels, statuses and the Needs attention list.
import type { CounterView, Ticket, TicketHistoryEvent } from './types'

/** GET /floor/series: buckets oldest first, the last ending now (store.FloorSeries). */
export interface FloorBucket {
  end: string
  waiting: number
  avgWaitSec: number
  longestWaitSec: number
  served: number
  transactions: number
  avgTransactionSec: number
  called: number
  calledWithin: number
}

export interface FloorSeries {
  stepSec: number
  buckets: FloorBucket[]
  transactionsToday: number
  avgTransactionSecToday: number
  queues: { queueId: string; transactions: number; avgTransactionSec: number }[]
}

/** Past this share of its target a waiting customer is close to the SLA. */
export const NEAR_SHARE = 0.8
/** Within SLA below this percentage turns its card red. */
export const SLA_ALERT_PCT = 80
/** One customer at a desk for longer than this is worth a look (store.LongServingAfter). */
export const LONG_SERVING_SEC = 15 * 60
/** How often the series is read again; live events never trigger it. */
export const SERIES_EVERY_MS = 60_000

/** Gaps filled with the last value seen; leading gaps with the first, else `fallback`. */
export function carryForward(values: readonly (number | null)[], fallback = 0): number[] {
  const first = values.find((v): v is number => v !== null)
  let last = first ?? fallback
  return values.map((v) => (v === null ? last : (last = v)))
}

/** What the live view knows now, laid over the series' last point. */
export interface LiveNow {
  waiting: number
  avgWaitSec: number
  longestWaitSec: number
}

export interface DashSeries {
  ends: string[]
  waiting: number[]
  served: number[]
  /** Served added up through the day's last four hours, for the Served today spark. */
  servedRunning: number[]
  avgWaitMin: number[]
  longestMin: number[]
  avgTxMin: number[]
  slaPct: number[]
  /** The last quarter hour's share called within target; null when nobody with a target was called yet. */
  slaNow: number | null
}

const minutes = (sec: number) => Math.round(sec / 60)

/** The series as the charts draw it, the last point replaced by the live figures. */
export function dashSeries(series: FloorSeries | null, live: LiveNow): DashSeries {
  const b = series?.buckets ?? []
  const n = b.length
  const waiting = b.map((x, i) => (i === n - 1 ? live.waiting : x.waiting))
  const avgWaitMin = b.map((x, i) => minutes(i === n - 1 ? live.avgWaitSec : x.avgWaitSec))
  const longestMin = b.map((x, i) => minutes(i === n - 1 ? live.longestWaitSec : x.longestWaitSec))
  const served = b.map((x) => x.served)
  let run = 0
  const servedRunning = served.map((v) => (run += v))
  const avgTxMin = carryForward(b.map((x) => (x.transactions ? minutes(x.avgTransactionSec) : null)))
  const sla = b.map((x) => (x.called ? Math.round((100 * x.calledWithin) / x.called) : null))
  const slaNow = [...sla].reverse().find((v): v is number => v !== null) ?? null
  return {
    ends: b.map((x) => x.end),
    waiting,
    served,
    servedRunning,
    avgWaitMin,
    longestMin,
    avgTxMin,
    slaPct: carryForward(sla, 100),
    slaNow,
  }
}

/** A round top for a chart's scale: 5, 10, 20, 50, 100… */
export function niceMax(v: number): number {
  if (v <= 5) return 5
  const p = 10 ** Math.floor(Math.log10(v))
  const n = v / p
  return (n <= 2 ? 2 : n <= 5 ? 5 : 10) * p
}

/** A chart's drawing area inside its viewBox. */
export interface Frame {
  w: number
  h: number
  l: number
  r: number
  t: number
  b: number
}

export interface Point {
  x: number
  y: number
}

const f1 = (v: number) => Number(v.toFixed(1))

/** Each value's place on a line chart scaled to `max`. */
export function linePoints(values: readonly number[], fr: Frame, max: number): Point[] {
  const pw = fr.w - fr.l - fr.r
  const ph = fr.h - fr.t - fr.b
  const step = values.length > 1 ? pw / (values.length - 1) : 0
  return values.map((v, i) => ({ x: f1(fr.l + i * step), y: f1(fr.t + ph - (Math.max(0, v) / max) * ph) }))
}

export function linePath(points: readonly Point[]): string {
  return points.map((p, i) => `${i ? 'L' : 'M'}${p.x} ${p.y}`).join(' ')
}

/** The line closed down to the baseline, for the soft fill under it. */
export function areaPath(points: readonly Point[], baseY: number): string {
  if (points.length === 0) return ''
  const last = points[points.length - 1]!
  return `${linePath(points)} L${last.x} ${baseY} L${points[0]!.x} ${baseY}Z`
}

/** A bar with rounded top corners, standing on y + h. */
export function barPath(x: number, y: number, w: number, h: number, r = 4): string {
  const base = f1(y + h)
  if (h <= 0) return ''
  const rr = Math.min(r, h, w / 2)
  return `M${f1(x)} ${base} V${f1(y + rr)} Q${f1(x)} ${f1(y)} ${f1(x + rr)} ${f1(y)} H${f1(x + w - rr)} Q${f1(x + w)} ${f1(y)} ${f1(x + w)} ${f1(y + rr)} V${base}Z`
}

/** Bars side by side across the frame; a non-zero value is at least 2 high. */
export function bars(values: readonly number[], fr: Frame, max: number, gap = 3) {
  const pw = fr.w - fr.l - fr.r
  const ph = fr.h - fr.t - fr.b
  const bw = values.length ? pw / values.length : 0
  return values.map((v, i) => {
    const h = Math.max(v > 0 ? 2 : 0, (v / max) * ph)
    const x = fr.l + i * bw + gap
    return { x: f1(fr.l + i * bw), slot: f1(bw), path: barPath(x, fr.t + ph - h, bw - gap * 2, h), centre: f1(fr.l + i * bw + bw / 2) }
  })
}

/** Which points carry a time under the axis: every third, and the last ("Now"). */
export function axisTicks(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i).filter((i) => i % 3 === 0 || i === n - 1)
}

/** A sparkline in a w × h box, scaled between the series' own low (or 0) and high. */
export function spark(values: readonly number[], w = 120, h = 34) {
  if (values.length === 0) return { line: '', area: '', last: { x: 0, y: h } }
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const step = values.length > 1 ? w / (values.length - 1) : 0
  const pts = values.map((v, i) => ({ x: f1(i * step), y: f1(h - 3 - ((v - min) / (max - min || 1)) * (h - 8)) }))
  const line = linePath(pts)
  return { line, area: `${line} L${w} ${h} L0 ${h}Z`, last: pts[pts.length - 1]! }
}

/** A donut's segments as stroke dashes on a circle of radius r: each part's
 *  length less a small gap, and where it starts. Empty parts draw nothing. */
export function donutDashes(parts: readonly number[], r: number, gap = 3) {
  const c = 2 * Math.PI * r
  const total = parts.reduce((a, v) => a + v, 0) || 1
  const g = parts.filter((v) => v > 0).length > 1 ? gap : 0
  let off = 0
  return parts.map((v) => {
    const len = (v / total) * c
    const seg = { length: f1(Math.max(0, len - g)), offset: f1(-off), circumference: f1(c), share: Math.round((v / total) * 100) }
    off += len
    return seg
  })
}

export type Level = 'good' | 'warning' | 'critical'

/** A waiting customer against their target: over it, past 80% of it, or fine. */
export function slaLevel(waitSec: number, targetSec: number): Level {
  if (!targetSec || targetSec <= 0) return 'good'
  if (waitSec > targetSec) return 'critical'
  if (waitSec > targetSec * NEAR_SHARE) return 'warning'
  return 'good'
}

/** How much of the target has been used, 0–100; 0 without a target. */
export function slaUsed(waitSec: number, targetSec: number): number {
  if (!targetSec || targetSec <= 0) return 0
  return Math.min(100, Math.round((waitSec / targetSec) * 100))
}

export type StatusKind = 'noStaff' | 'over' | 'near' | 'onTrack'
export const STATUS_TONE: Record<StatusKind, 'critical' | 'serious' | 'warning' | 'good'> = {
  noStaff: 'critical',
  over: 'serious',
  near: 'warning',
  onTrack: 'good',
}

/** A queue's pill: people and no desk calling it, then over target, then close. */
export function queueStatus(q: { waiting: number; open: number; over: number; near: number }, callersKnown = true): StatusKind {
  if (q.waiting > 0 && q.open === 0 && callersKnown) return 'noStaff'
  if (q.over > 0) return 'over'
  if (q.near > 0) return 'near'
  return 'onTrack'
}

export type DeskState = 'serving' | 'free' | 'break' | 'closed'

/** A desk as the dashboard counts it: a customer at it (called, served or wrapping up) is Serving. */
export function deskState(d: Pick<CounterView, 'state' | 'ticketId' | 'displayNumber'>): DeskState {
  if (d.state === 'closed') return 'closed'
  if (d.state === 'paused') return 'break'
  return d.ticketId && d.displayNumber ? 'serving' : 'free'
}

export type DeskCounts = Record<DeskState, number>

export function deskCounts(desks: readonly Pick<CounterView, 'state' | 'ticketId' | 'displayNumber'>[]): DeskCounts {
  const out: DeskCounts = { serving: 0, free: 0, break: 0, closed: 0 }
  for (const d of desks) out[deskState(d)]++
  return out
}

/** The same split from the All branches totals, which count customers at desks, not desks. */
export function overviewDesks(t: { desks: number; desksOpen: number; desksOnBreak: number; serving: number }): DeskCounts {
  const working = Math.max(0, t.desksOpen - t.desksOnBreak)
  const serving = Math.min(t.serving, working)
  return { serving, free: working - serving, break: t.desksOnBreak, closed: Math.max(0, t.desks - t.desksOpen) }
}

export type Attention =
  | { kind: 'noStaff'; level: 'critical'; name: string; waiting: number }
  | { kind: 'noDesk'; level: 'critical'; name: string; waiting: number }
  | { kind: 'over'; level: 'serious'; n: number }
  | { kind: 'near'; level: 'warning'; n: number }
  | { kind: 'long'; level: 'warning'; n: number; who: string[] }

/**
 * What needs a supervisor, worst first: a line nobody calls (a branch with no
 * desk open, in All branches), customers over their target, customers close to
 * it, desks long with one customer. `who` names at most three.
 */
export function needsAttention(input: {
  unstaffed?: { name: string; waiting: number }[]
  closedBranches?: { name: string; waiting: number }[]
  over: number
  near: number
  long: { n: number; who: string[] }
}): Attention[] {
  const out: Attention[] = []
  for (const q of input.unstaffed ?? []) out.push({ kind: 'noStaff', level: 'critical', ...q })
  for (const b of input.closedBranches ?? []) out.push({ kind: 'noDesk', level: 'critical', ...b })
  if (input.over > 0) out.push({ kind: 'over', level: 'serious', n: input.over })
  if (input.near > 0) out.push({ kind: 'near', level: 'warning', n: input.near })
  if (input.long.n > 0) out.push({ kind: 'long', level: 'warning', n: input.long.n, who: input.long.who.slice(0, 3) })
  return out
}

/** A queue's or a branch's colour, by its place in a stable order. */
export function seriesColour(i: number): string {
  return `var(--series-${(((i % 8) + 8) % 8) + 1})`
}

/** 1st, 2nd, 3rd, 4th, 11th, 12th, 13th, 21st. English only, as the staff UI is for now. */
export function ordinal(n: number): string {
  const t = n % 100
  const s = t >= 11 && t <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th'
  return `${n}${s}`
}

/** A ticket's number without its queue letter, which the tile beside it shows: "L-004" → "004". */
export function numberPart(displayNumber: string, letter: string): string {
  if (letter && displayNumber.toUpperCase().startsWith(letter.toUpperCase())) {
    const rest = displayNumber.slice(letter.length).replace(/^[-\s]/, '')
    if (rest) return rest
  }
  return displayNumber
}

export type Freshness = { kind: 'live' | 'connecting' | 'offline' } | { kind: 'paused'; at: number }

/**
 * The pill beside the branch picker. Live only while the data is arriving: the
 * branch's socket is live, or the All branches poll last answered. With
 * Auto-refresh off it names the moment of the last update, which does not tick.
 */
export function freshness(input: { connected: boolean; failed: boolean; auto: boolean; updatedAt: number }): Freshness {
  if (input.failed) return { kind: 'offline' }
  if (!input.updatedAt || (!input.connected && input.auto)) return { kind: 'connecting' }
  return input.auto ? { kind: 'live' } : { kind: 'paused', at: input.updatedAt }
}

export type JourneyKind = 'issued' | 'booked' | 'firstCalled' | 'lastCalled' | 'started' | 'ended' | 'parked' | 'missed'
export interface JourneyStep {
  at: string
  kind: JourneyKind
  serviceId?: string
  counterId?: string
}

type JourneyTicket = Pick<
  Ticket,
  'issuedAt' | 'appointmentAt' | 'firstCalledAt' | 'lastCalledAt' | 'transactions' | 'parkedAt' | 'state' | 'calledToCounterId'
>

/** A visit's path so far, oldest first, from what the ticket itself carries. */
export function journeySteps(t: JourneyTicket): JourneyStep[] {
  const out: JourneyStep[] = [{ at: t.issuedAt, kind: 'issued' }]
  if (t.appointmentAt) out.push({ at: t.appointmentAt, kind: 'booked' })
  if (t.firstCalledAt) out.push({ at: t.firstCalledAt, kind: 'firstCalled' })
  if (t.lastCalledAt && t.lastCalledAt !== t.firstCalledAt && t.state !== 'no_show') {
    out.push({ at: t.lastCalledAt, kind: 'lastCalled', counterId: t.calledToCounterId })
  }
  for (const x of t.transactions) {
    if (x.startedAt) out.push({ at: x.startedAt, kind: 'started', serviceId: x.serviceId, counterId: x.counterId })
    if (x.endedAt) out.push({ at: x.endedAt, kind: 'ended', serviceId: x.serviceId, counterId: x.counterId })
  }
  if (t.state === 'parked' && t.parkedAt) out.push({ at: t.parkedAt, kind: 'parked' })
  if (t.state === 'no_show' && t.lastCalledAt) out.push({ at: t.lastCalledAt, kind: 'missed', counterId: t.calledToCounterId })
  return out
    .map((s, i) => ({ s, i }))
    .sort((a, b) => Date.parse(a.s.at) - Date.parse(b.s.at) || a.i - b.i)
    .map(({ s }) => s)
}

// The history events a journey step stands for.
const STEP_EVENTS: Record<JourneyKind, string[]> = {
  issued: ['ticket.issued'],
  booked: [],
  firstCalled: ['ticket.called'],
  lastCalled: ['ticket.called', 'ticket.recalled'],
  started: ['ticket.serving'],
  ended: ['ticket.closed', 'ticket.transferred', 'ticket.service_advanced', 'ticket.returned'],
  parked: ['ticket.parked'],
  missed: ['ticket.no_show'],
}

export interface TokenAction {
  at: string
  kind: string
  token: string
}

/** Which journey steps an access token took (the token's name, by step), and the
 *  token's actions that no step shows. A step matches an event of its kind within
 *  two seconds; a miss is marked later than its call, so it matches by kind. */
export function tokenMarks(
  steps: JourneyStep[],
  events: Pick<TicketHistoryEvent, 'kind' | 'at' | 'viaToken'>[],
): { onStep: (string | undefined)[]; other: TokenAction[] } {
  const onStep: (string | undefined)[] = steps.map(() => undefined)
  const other: TokenAction[] = []
  for (const e of events) {
    if (!e.viaToken) continue
    const at = Date.parse(e.at)
    const i = steps.findIndex(
      (s, i) =>
        onStep[i] === undefined &&
        STEP_EVENTS[s.kind].includes(e.kind) &&
        (s.kind === 'missed' || Math.abs(Date.parse(s.at) - at) < 2000),
    )
    if (i >= 0) onStep[i] = e.viaToken.name
    else other.push({ at: e.at, kind: e.kind, token: e.viaToken.name })
  }
  return { onStep, other }
}
